import { SxProps, Theme } from '@mui/material';

export type DpModernProductCardVariant = 'mini' | 'medium' | 'large' | 'largeRepurchase';

type ResponsiveValue<T> = T | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', T>>;

export type VariantTokens = {
	cardWidth: ResponsiveValue<string>;
	cardHeight?: ResponsiveValue<string>;
	borderRadius: string;
	imageHeight: ResponsiveValue<string>;
	imageFit: 'cover' | 'contain';
	showRibbons: boolean;
	infoPadding: string;
	infoGap?: string;
	titleFontSize: ResponsiveValue<string>;
	titleFontWeight: number;
	titleLineHeight: string;
	titleMinHeight: string | ResponsiveValue<string>;
	titleMarginBottom?: string;
	categoryFontSize: ResponsiveValue<string>;
	categoryMarginBottom?: string;
	priceGap: string;
	priceFontSize: ResponsiveValue<string>;
	priceFontWeight: number;
	pushPriceToBottom: boolean;
	skeletonWidth: number;
	skeletonHeight: number;
};

const LARGE: VariantTokens = {
	cardWidth: { xs: '180px', sm: '290px' },
	cardHeight: { xs: '350px', sm: '460px' },
	borderRadius: '0px',
	imageHeight: { xs: '178px', sm: '290px' },
	imageFit: 'contain',
	showRibbons: true,
	infoPadding: '10px 15px',
	titleFontSize: { xs: 'var(--font-size-default)', sm: 'var(--font-size-body)' },
	titleFontWeight: 600,
	titleLineHeight: '20px',
	titleMinHeight: '0px',
	titleMarginBottom: '4px',
	categoryFontSize: 'var(--font-size-default)',
	categoryMarginBottom: '11px',
	priceGap: '8px',
	priceFontSize: 'var(--font-size-body)',
	priceFontWeight: 600,
	pushPriceToBottom: false,
	skeletonWidth: 100,
	skeletonHeight: 20,
};

export const VARIANTS: Record<DpModernProductCardVariant, VariantTokens> = {
	mini: {
		cardWidth: '130px',
		borderRadius: '6px',
		imageHeight: '130px',
		imageFit: 'cover',
		showRibbons: false,
		infoPadding: '8px 10px 10px',
		titleFontSize: 'var(--font-size-default)',
		titleFontWeight: 600,
		titleLineHeight: '17px',
		titleMinHeight: '34px',
		titleMarginBottom: '4px',
		categoryFontSize: 'var(--font-size-small)',
		categoryMarginBottom: '6px',
		priceGap: '6px',
		priceFontSize: 'var(--font-size-default)',
		priceFontWeight: 600,
		pushPriceToBottom: false,
		skeletonWidth: 60,
		skeletonHeight: 14,
	},
	medium: {
		cardWidth: '190px',
		cardHeight: '325px',
		borderRadius: '0px',
		imageHeight: '190px',
		imageFit: 'contain',
		showRibbons: true,
		infoPadding: '14px 16px',
		infoGap: '8px',
		titleFontSize: 'var(--font-size-body)',
		titleFontWeight: 600,
		titleLineHeight: '20px',
		titleMinHeight: '40px',
		categoryFontSize: 'var(--font-size-default)',
		priceGap: '8px',
		priceFontSize: 'var(--font-size-body)',
		priceFontWeight: 700,
		pushPriceToBottom: true,
		skeletonWidth: 80,
		skeletonHeight: 16,
	},
	large: LARGE,
	largeRepurchase: {
		...LARGE,
		cardHeight: { xs: '375px', sm: '490px' },
	},
};

export const cardSX = (t: VariantTokens): SxProps<Theme> => ({
	width: t.cardWidth,
	height: t.cardHeight,
	flex: '0 0 auto',
	border: '1px solid #DADCE0',
	borderRadius: t.borderRadius,
	overflow: 'hidden',
	backgroundColor: '#FFFFFF',
	display: 'flex',
	flexDirection: 'column',
});

export const linkableSX: SxProps<Theme> = {
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
	width: '100%',
	textDecoration: 'none',
};

export const imageFrameSX = (t: VariantTokens): SxProps<Theme> => ({
	position: 'relative',
	height: t.imageHeight,
	width: '100%',
	flex: '0 0 auto',
	backgroundColor: '#F1F1F1',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const imageSX = (t: VariantTokens): SxProps<Theme> => ({
	width: t.imageFit === 'contain' ? t.imageHeight : '100%',
	height: t.imageFit === 'contain' ? t.imageHeight : '100%',
	objectFit: t.imageFit,
});

export const infoSX = (t: VariantTokens): SxProps<Theme> => ({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	padding: t.infoPadding,
	gap: t.infoGap,
});

export const titleSX = (t: VariantTokens): SxProps<Theme> => ({
	color: '#2C2C2C',
	fontSize: t.titleFontSize,
	fontWeight: t.titleFontWeight,
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	lineHeight: t.titleLineHeight,
	minHeight: t.titleMinHeight,
	mb: t.titleMarginBottom,
});

export const categorySX = (t: VariantTokens): SxProps<Theme> => ({
	color: '#505050',
	fontSize: t.categoryFontSize,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	mb: t.categoryMarginBottom,
});

export const priceRowSX = (t: VariantTokens): SxProps<Theme> => ({
	display: 'flex',
	gap: t.priceGap,
	alignItems: 'center',
	marginTop: t.pushPriceToBottom ? 'auto' : undefined,
});

export const discountSX = (t: VariantTokens): SxProps<Theme> => ({
	color: '#AB2326',
	fontSize: t.priceFontSize,
	fontWeight: t.priceFontWeight,
});

export const priceSX = (t: VariantTokens, hasDiscount: boolean): SxProps<Theme> => ({
	color: hasDiscount ? '#C2C2C2' : '#2C2C2C',
	fontSize: t.priceFontSize,
	fontWeight: t.priceFontWeight,
	textDecoration: hasDiscount ? 'line-through' : 'none',
});

export const priceNoteSX: SxProps<Theme> = {
	mt: '6px',
	display: 'flex',
	alignItems: 'center',
	gap: '6px',
	color: '#0B6AD9',
	fontSize: '13px',
	fontWeight: 700,
	lineHeight: 1.3,
};
